/**
 * Reglas de cálculo para la facturación masiva de arriendos.
 *
 * - Monto = mínimo (UF) × m² del contrato × valor UF del día de liquidación
 *   (en la glosa: "1 UF × 50,5 m² × $39.153,69"). La UF se toma del día
 *   `dia_uf` del contrato; si falta, el día que indica la liquidación.
 * - Variable: porcentaje × venta del mes anterior. Con operador OPCION se cobra
 *   solo lo que supera al arriendo fijo; con MAS o SIN MINIMO, el variable completo.
 *   Sin venta cargada, el ítem queda pendiente en $0.
 * - Promoción con origen "ALQUILER": porcentaje sobre el arriendo (fijo + variable).
 */

export { aFecha } from "./fechas";

export const IVA_RATE = 0.19;

const DIVISIONES_FIJAS = ["ALQUILER FIJO", "GASTOS COMUNES", "FDO. PROMOCION FIJO", "OTRO COBRO"];
const DIVISIONES_VARIABLES = ["ALQUILER VARIABLE", "FDO. PROMOCION VARIABLE"];

export interface DivisionCalculo {
  nombre_division: string;
  minimo: number | string;
  indexacion: string;
  operador?: string;
  origen?: string;
  /** Variable: % sobre ventas. Promoción con origen ALQUILER: % sobre el arriendo. 0,06 = 6%. */
  porcentaje?: number | string;
  /** Otro Cobro: qué se cobra (bodega, internet…), si es por m² o un monto fijo, y si va sin IVA. */
  descripcion?: string;
  base?: string; // "MONTO FIJO" | "POR M2"
  exento?: boolean;
}

export interface ItemCalculado {
  glosa: string;
  montoNeto: number;
  division: string;
  montoUF?: number;
  valorUF?: number;
  pendiente?: boolean;
  /** Exento de IVA (p. ej. internet): no suma IVA y va en factura exenta en Softland. */
  exento?: boolean;
}

export const OTRO_COBRO = "Otro Cobro";
const esOtroCobro = (nombre: string) => normalizar(nombre) === "OTRO COBRO";
/** "Otro Cobro" por monto fijo multiplica por 1 en vez de por los m² del contrato. */
const porM2 = (division: DivisionCalculo) => !esOtroCobro(division.nombre_division) || normalizar(division.base ?? "") === "POR M2";

/** Mayúsculas, sin tildes ni espacios sobrantes. */
export function normalizar(texto: string): string {
  return (texto ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toUpperCase();
}

/** Convierte números guardados como texto ("0,35", "1.200,5") o number. */
export function aNumero(valor: unknown): number {
  if (typeof valor === "number") return isFinite(valor) ? valor : 0;
  if (typeof valor !== "string" || !valor.trim()) return 0;
  let texto = valor.trim();
  if (texto.includes(",")) texto = texto.replace(/\./g, "").replace(",", ".");
  const numero = Number(texto);
  return isFinite(numero) ? numero : 0;
}

/** Formato chileno: miles con punto, decimales con coma, sin ceros sobrantes. */
export function formatearNumero(valor: number, maxDecimales = 2): string {
  const [entero, decimales] = Math.abs(valor).toFixed(maxDecimales).split(".");
  const miles = entero.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const resto = (decimales ?? "").replace(/0+$/, "");
  return `${valor < 0 ? "-" : ""}${miles}${resto ? `,${resto}` : ""}`;
}

export function esDivisionFija(nombre: string): boolean {
  return DIVISIONES_FIJAS.includes(normalizar(nombre));
}

export function esDivisionVariable(nombre: string): boolean {
  return DIVISIONES_VARIABLES.includes(normalizar(nombre));
}

/** Día de la UF según el texto de la liquidación, p. ej. "GENERAL UF DIA 5" → 5. */
export function diaUfDesdeLiquidacion(liquidacion: string): number | null {
  const match = normalizar(liquidacion).match(/DIA\s+(\d{1,2})/);
  if (!match) return null;
  const dia = Number(match[1]);
  return dia >= 1 && dia <= 31 ? dia : null;
}

/** Fecha de la UF para un periodo "YYYY-MM"; el día se ajusta al último del mes si no existe. */
export function fechaUf(periodo: string, dia: number): Date {
  const [year, month] = periodo.split("-").map(Number);
  const ultimoDia = new Date(year, month, 0).getDate();
  return new Date(year, month - 1, Math.min(Math.max(dia, 1), ultimoDia));
}

export interface Proporcion {
  dias: number;
  diasMes: number;
}

/**
 * Días del periodo "YYYY-MM" cubiertos a la vez por todas las vigencias dadas
 * (contrato, condición…). Fechas nulas = abiertas. Para prorratear los cargos fijos.
 */
export function proporcionDelMes(
  periodo: string,
  vigencias: Array<{ desde: Date | null; hasta: Date | null }>
): Proporcion {
  const [year, month] = periodo.split("-").map(Number);
  const diasMes = new Date(year, month, 0).getDate();
  let primero = 1;
  let ultimo = diasMes;
  for (const { desde, hasta } of vigencias) {
    if (desde) {
      if (desde.getFullYear() > year || (desde.getFullYear() === year && desde.getMonth() + 1 > month))
        return { dias: 0, diasMes };
      if (desde.getFullYear() === year && desde.getMonth() + 1 === month)
        primero = Math.max(primero, desde.getDate());
    }
    if (hasta) {
      if (hasta.getFullYear() < year || (hasta.getFullYear() === year && hasta.getMonth() + 1 < month))
        return { dias: 0, diasMes };
      if (hasta.getFullYear() === year && hasta.getMonth() + 1 === month)
        ultimo = Math.min(ultimo, hasta.getDate());
    }
  }
  return { dias: Math.max(0, ultimo - primero + 1), diasMes };
}

/**
 * Combina el cargo fijo con el variable según el operador de la división:
 * OPCION = el mayor de los dos · MAS = la suma · SIN MINIMO = solo el variable.
 */
export function aplicarOperador(fijo: number, variable: number, operador: string): number {
  const op = normalizar(operador);
  if (op === "OPCION") return Math.max(fijo, variable);
  if (op === "MAS") return fijo + variable;
  if (op === "SIN MINIMO") return variable;
  return fijo;
}

/** Totales de una factura a partir de sus ítems. Los ítems exentos no llevan IVA. */
export function totalesDeItems(items: Array<{ montoNeto: number; exento?: boolean }>): {
  montoNeto: number;
  montoIva: number;
  montoTotal: number;
  montoExento?: number;
} {
  const suma = (lista: Array<{ montoNeto: number }>) => lista.reduce((s, item) => s + (Number(item.montoNeto) || 0), 0);
  const montoNeto = suma(items);
  const montoExento = suma(items.filter((i) => i.exento));
  const montoIva = calcularIva(montoNeto - montoExento);
  return { montoNeto, montoIva, montoTotal: montoNeto + montoIva, ...(montoExento ? { montoExento } : {}) };
}

/** Indica si la vigencia [desde, hasta] toca el periodo [inicio, fin]. Fechas nulas = abiertas. */
export function vigenteEnPeriodo(
  desde: Date | null,
  hasta: Date | null,
  inicio: Date,
  fin: Date
): boolean {
  if (desde && desde > fin) return false;
  if (hasta && hasta < inicio) return false;
  return true;
}

/** Indica si la división se cobra en UF (en caso contrario el mínimo ya está en pesos). */
export function indexaEnUf(indexacion: string): boolean {
  const texto = normalizar(indexacion);
  return texto.includes("UNIDAD DE FOMENTO") || /\bUF\b/.test(texto);
}

/** Indica si la división necesita la UF para calcularse. */
export function requiereUf(division: DivisionCalculo): boolean {
  return esDivisionFija(division.nombre_division) && indexaEnUf(division.indexacion);
}

/**
 * Calcula el ítem de factura de una división.
 * `valorUF` solo se usa cuando la división está indexada a UF.
 */
export function calcularItemDivision(
  division: DivisionCalculo,
  superficie: number,
  valorUF: number | null,
  periodo: string,
  proporcion?: Proporcion
): ItemCalculado {
  const otro = esOtroCobro(division.nombre_division);
  // Un "Otro Cobro" se presenta con su descripción ("Bodega", "Internet").
  const nombre = otro && division.descripcion ? division.descripcion : division.nombre_division;
  const exento = otro && !!division.exento ? { exento: true } : {};
  const textoExento = exento.exento ? " · exento de IVA" : "";

  if (!esDivisionFija(division.nombre_division)) {
    let motivo = esDivisionVariable(nombre) ? "pendiente de ventas" : "división no reconocida";
    if (esDivisionVariable(nombre) && normalizar(division.operador ?? "") === "OPCION")
      motivo += "; se cobra si supera el fijo";
    return {
      glosa: `${nombre} - ${periodo} (${motivo})`,
      montoNeto: 0,
      division: nombre,
      pendiente: true,
    };
  }

  // Prorrateo: contrato o condición que parte o termina dentro del mes.
  const parcial = proporcion && proporcion.dias < proporcion.diasMes ? proporcion : null;
  const factor = parcial ? parcial.dias / parcial.diasMes : 1;
  const textoDias = parcial ? ` × ${parcial.dias}/${parcial.diasMes} días` : "";

  const minimo = aNumero(division.minimo);
  const multiplicador = porM2(division) ? superficie : 1;
  const base = minimo * multiplicador * factor;
  const m2 = porM2(division) ? ` × ${formatearNumero(superficie)} m²` : "";

  if (indexaEnUf(division.indexacion)) {
    if (!valorUF) throw new Error(`Falta el valor UF para ${nombre}`);
    const montoUF = Math.round(base * 10000) / 10000;
    const detalle = `${formatearNumero(minimo, 4)} UF${m2}${textoDias} × $${formatearNumero(valorUF)}`;
    return {
      glosa: `${nombre} - ${periodo} (${detalle})${textoExento}`,
      montoNeto: Math.round(base * valorUF),
      division: division.nombre_division,
      montoUF,
      valorUF,
      ...exento,
    };
  }

  return {
    glosa: `${nombre} - ${periodo} ($${formatearNumero(minimo, 4)}${m2}${textoDias})${textoExento}`,
    montoNeto: Math.round(base),
    division: division.nombre_division,
    ...exento,
  };
}

const esPromocion = (nombre: string) => normalizar(nombre).startsWith("FDO. PROMOCION");
const esAlquilerFijo = (nombre: string) => normalizar(nombre) === "ALQUILER FIJO";
const esAlquilerVariable = (nombre: string) => normalizar(nombre) === "ALQUILER VARIABLE";

/**
 * Promoción como % del arriendo en vez de UF × m². Origen "ALQUILER" = sobre fijo + variable;
 * "ALQUILER FIJO" = solo sobre el fijo (lo más común en los contratos del mall).
 */
export function promocionSobreAlquiler(division: DivisionCalculo): boolean {
  const origen = normalizar(division.origen ?? "");
  return esPromocion(division.nombre_division) && (origen === "ALQUILER" || origen === "ALQUILER FIJO");
}

const promocionSoloFijo = (division: DivisionCalculo) => normalizar(division.origen ?? "") === "ALQUILER FIJO";

const formatearPct = (pct: number) => `${formatearNumero(pct * 100, 2)}%`;

/**
 * Ítems de una condición comercial. Orden: cargos fijos, variable (necesita el fijo
 * para OPCION) y promoción sobre alquiler (necesita fijo + variable).
 * `venta` es la venta del mes anterior del locatario; null si no está cargada.
 */
export function calcularItemsCondicion(
  divisiones: DivisionCalculo[],
  contexto: {
    superficie: number;
    valorUF: number | null;
    periodo: string;
    proporcion?: Proporcion;
    venta: number | null;
    periodoVenta?: string;
    /** Fijo del mes de la venta: OPCION compara el variable contra ese fijo. */
    fijoMesAnterior?: number;
  }
): ItemCalculado[] {
  const { superficie, valorUF, periodo, proporcion, venta, periodoVenta, fijoMesAnterior } = contexto;
  const items: ItemCalculado[] = [];

  // 1. Cargos fijos (y promoción en UF × m² si no es sobre alquiler)
  for (const d of divisiones) {
    if (esDivisionFija(d.nombre_division) && !promocionSobreAlquiler(d))
      items.push(calcularItemDivision(d, superficie, valorUF, periodo, proporcion));
  }
  const fijo = items
    .filter((i) => esAlquilerFijo(i.division))
    .reduce((s, i) => s + i.montoNeto, 0);

  // 2. Variable sobre ventas
  for (const d of divisiones.filter((x) => esAlquilerVariable(x.nombre_division))) {
    const pct = aNumero(d.porcentaje);
    if (venta === null || !pct) {
      items.push(calcularItemDivision(d, superficie, valorUF, periodo)); // pendiente
      continue;
    }
    const variable = Math.round(pct * venta);
    const op = normalizar(d.operador ?? "");
    const ventaTxt = `${formatearPct(pct)} × venta ${periodoVenta ?? ""} $${formatearNumero(venta)}`.replace("  ", " ");
    if (op === "OPCION") {
      const base = fijoMesAnterior ?? fijo;
      const diferencia = Math.max(0, variable - base);
      items.push({
        glosa: `${d.nombre_division} - ${periodo} (${ventaTxt} = $${formatearNumero(variable)}; sobre fijo ${periodoVenta ?? ""} $${formatearNumero(base)})`.replace("  ", " "),
        montoNeto: diferencia,
        division: d.nombre_division,
      });
    } else {
      items.push({
        glosa: `${d.nombre_division} - ${periodo} (${ventaTxt})`,
        montoNeto: variable,
        division: d.nombre_division,
      });
    }
  }

  // 3. Promoción como % del arriendo: solo fijo, o fijo + variable cobrado
  const variableCobrado = items
    .filter((i) => esAlquilerVariable(i.division))
    .reduce((s, i) => s + i.montoNeto, 0);
  const variablePendiente = items.some((i) => esAlquilerVariable(i.division) && i.pendiente);
  for (const d of divisiones.filter(promocionSobreAlquiler)) {
    const pct = aNumero(d.porcentaje);
    const soloFijo = promocionSoloFijo(d);
    const base = soloFijo ? fijo : fijo + variableCobrado;
    const pendiente = !soloFijo && variablePendiente;
    items.push({
      glosa: `${d.nombre_division} - ${periodo} (${formatearPct(pct)} × ${soloFijo ? "fijo" : "arriendo"} $${formatearNumero(base)}${pendiente ? "; falta el variable" : ""})`,
      montoNeto: Math.round(pct * base),
      division: d.nombre_division,
      ...(pendiente ? { pendiente: true } : {}),
    });
  }

  // Divisiones que no son fijas, variables ni promoción sobre alquiler (p. ej. no reconocidas)
  for (const d of divisiones) {
    const n = d.nombre_division;
    if (!esDivisionFija(n) && !esAlquilerVariable(n) && !promocionSobreAlquiler(d))
      items.push(calcularItemDivision(d, superficie, valorUF, periodo));
  }
  return items;
}

/** Periodo anterior: "2026-07" → "2026-06" (la venta que se usa para el variable). */
export function periodoAnterior(periodo: string): string {
  const [y, m] = periodo.split("-").map(Number);
  const fecha = new Date(y, m - 2, 1);
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, "0")}`;
}

export function calcularIva(montoNeto: number): number {
  return Math.round(montoNeto * IVA_RATE);
}
